///
/// EarthBrowser
///
/// Created by Matt Giger on 6/6/2026
/// Copyright (c) 2026 Geodata Labs
///

/**
 * Time-based exponential low-pass filter for smoothing noisy pointer input.
 *
 * The smoothing factor is derived from a cutoff frequency and the elapsed time
 * between samples, so behavior is consistent regardless of event cadence. A
 * higher cutoff means less smoothing (more responsive); a lower cutoff means
 * more smoothing. Because the output converges to the input when the input
 * stops changing, this smooths motion without leaving a permanent lag.
 */
export class LowPassFilter {
	private value?: number
	private lastTimeMs?: number

	constructor(private cutoffHz: number) {}

	/** Clear filter state so the next sample is passed through unchanged. */
	public reset(): void {
		this.value = undefined
		this.lastTimeMs = undefined
	}

	/**
	 * Filter a raw value at the given timestamp (milliseconds).
	 */
	public filter(raw: number, timeMs: number): number {
		if (this.value === undefined || this.lastTimeMs === undefined) {
			this.value = raw
			this.lastTimeMs = timeMs
			return raw
		}

		const dt = Math.max((timeMs - this.lastTimeMs) / 1000, 1e-4)
		const tau = 1 / (2 * Math.PI * this.cutoffHz)
		const alpha = dt / (tau + dt)

		this.value = this.value + alpha * (raw - this.value)
		this.lastTimeMs = timeMs
		return this.value
	}
}
